import { useState, useCallback, useLayoutEffect, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { geoMercator } from "d3-geo";
import { ChevronLeft, ChevronRight, Loader2, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { normalizeGeoName, STATE_CENTERS, DISTRICT_DB_TO_TOPO } from "../data/geoMappings";
import stateDistrictsMap from "../data/stateDistrictsMap.json";
import districtBlocksMap from "../data/districtBlocksMap.json";
import districtCentroids from "../data/districtCentroids.json";
import { fmtCr, fmtNum } from "../utils/format";
import { getThemeDotColor } from "../utils/dynamicColors";
import {
  STATES_URL, DISTRICTS_URL, BLOCKS_URL_TPL,
  INDIA_CENTER, INDIA_SCALE, STATE_SCALES, HEAT_PALETTE, BUCKET_THRESHOLDS,
  makeScale, buildStateLookup, buildStateDataMap,
  buildDistrictLookup, buildAllDistrictLookup, buildAllDistrictDataMap,
  buildBlockLookup, buildDistrictDataMap, buildBlockDataMap,
} from "../utils/mapUtils";


const IndiaMap = forwardRef(function IndiaMap({
  stateData = [],
  districtData = [],
  blockData = [],
  allDistrictData = [],
  onStateSelect,
  onDistrictSelect,
  selectedState,
  selectedDistrict,
  selectedDistShapeID,
  dataLoading = false,
  mapViewMode = "states",
  onMapViewChange,
  showZoomControls = false,
  showDisbursed = true,   // Ch17 (Sep 10, 2026): false only for `/grants` Grant-Geography map where disbursed data is 0-ish.
}, ref) {
  const [tooltip, setTooltip] = useState(null);
  const [tooltipData, setTooltipData] = useState(null); // Full data for rich tooltip
  const [aspirationalOnly, setAspirationalOnly] = useState(false); // Filter for aspirational districts only
  // Colour mode: 'buckets' = fixed 7-tier thresholds · 'continuous' = quantile-based smooth gradient
  const [colorMode, setColorMode] = useState("continuous");

  // Reset aspirational filter when state changes (avoids stale filter from previous state)
  useEffect(() => {
    setAspirationalOnly(false);
  }, [selectedState]);
  // Loading overlay: shown during level transitions AND initial fetch
  const [mapLoading, setMapLoading] = useState(true);   // TRUE on mount — blank until geo loads
  const [loadingLabel, setLoadingLabel] = useState("Loading India map…");
  const loadingTimerRef = useRef(null);
  const geoReadyRef = useRef(false);         // set true after first successful Geographies render
  const tooltipThrottleRef = useRef(null);   // for throttling hover updates
  // ── Initial load: pre-fetch the top-level TopoJSON so we know when to hide the loader ──
  useEffect(() => {
    // react-simple-maps fetches internally; we pre-fetch to detect readiness
    // (browser cache means the second fetch is instant)
    fetch(STATES_URL)
      .then(() => {
        geoReadyRef.current = true;
        setMapLoading(false);
      })
      .catch(() => setMapLoading(false));
  }, []); // run once on mount
  const [userScale, setUserScale] = useState(1);

  // Expose zoom controls to parent
  useImperativeHandle(ref, () => ({
    zoomIn: () => setUserScale(s => Math.min(s * 1.6, 16)),
    zoomOut: () => setUserScale(s => Math.max(s / 1.6, 0.25)),
    resetZoom: () => setUserScale(1),
  }), []);

  // District centroid: set when user clicks on map OR looked up from pre-computed table
  const [districtCenter, setDistrictCenter] = useState(null);

  // level: "state" | "district" | "block" | "all-districts"
  const level = selectedDistrict ? "block"
    : selectedState ? "district"
    : (mapViewMode === "districts") ? "all-districts"
    : "state";

  const blocksUrl = selectedState
    ? BLOCKS_URL_TPL(normalizeGeoName(selectedState))
    : null;

  // Look up district centroid from pre-computed table (instant, no timing issues)
  const getDistrictCentroid = (districtName) => {
    if (!districtName) return null;
    const rawKey = districtName.toLowerCase().trim();
    // Try direct match first
    if (districtCentroids[rawKey]) return districtCentroids[rawKey];
    // Try via DB→Topo mapping
    const topoName = DISTRICT_DB_TO_TOPO[rawKey];
    if (topoName) {
      const topoKey = topoName.toLowerCase().trim();
      if (districtCentroids[topoKey]) return districtCentroids[topoKey];
    }
    // Try normalised (strip diacritics)
    const normKey = normalizeGeoName(districtName);
    if (districtCentroids[normKey]) return districtCentroids[normKey];
    return null;
  };

  // ── Base projection per level (center + scale) ──
  const baseProj = useMemo(() => {
    // Block level: zoom into the selected district
    if (level === "block" && selectedDistrict) {
      const center = districtCenter || getDistrictCentroid(selectedDistrict);
      if (center) {
        const stateScale = STATE_SCALES[selectedState] || INDIA_SCALE;
        return { center, scale: stateScale * 5 };
      }
    }
    // District level: zoom into the selected state
    if (level !== "state" && selectedState) {
      const center = STATE_CENTERS[selectedState];
      const scale  = STATE_SCALES[selectedState];
      if (center && scale) return { center, scale };
    }
    return { center: INDIA_CENTER, scale: INDIA_SCALE };
  }, [level, selectedState, selectedDistrict, districtCenter]);

  // Detect transitions and trigger loading + zoom reset
  const transitionKey = `${level}-${selectedState || ''}-${selectedDistrict || ''}`;
  const prevKey = useRef(transitionKey);
  // During render: detect prop change and set loading (React 18 derived-state pattern)
  if (prevKey.current !== transitionKey) {
    prevKey.current = transitionKey;
  }
  useEffect(() => {
    // This fires after every transition key change (state → district → block clicks)
    setUserScale(1);
    if (level !== "block") setDistrictCenter(null);
    setTooltip(null);
    // Show loading (minimum 600ms for visual stability)
    const label = level === "block" ? "Loading blocks…"
      : (level === "district" || level === "all-districts") ? "Loading districts…"
      : "Loading map…";
    setLoadingLabel(label);
    setMapLoading(true);
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    loadingTimerRef.current = setTimeout(() => setMapLoading(false), 600);
    return () => { if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionKey]);

  // Final projection: custom D3 geoMercator 
  const projFn = useMemo(() => {
    const scale = baseProj.scale * userScale;
    return geoMercator()
      .center(baseProj.center)
      .scale(scale)
      .translate([360, 240]); // Shifted left by 50px to give more space for tooltip on the right
  }, [baseProj, userScale]);

  // Build lookups & colour scales — MEMOIZED so they don't recompute on every tooltip hover
  const stateLookup    = useMemo(() => buildStateLookup(stateData),    [stateData]);
  const stateDataMap   = useMemo(() => buildStateDataMap(stateData),   [stateData]);
  const districtLookup = useMemo(() => buildDistrictLookup(districtData), [districtData]);
  const districtDataMap = useMemo(() => buildDistrictDataMap(districtData), [districtData]);

  // Filtered state-drilldown district lookups (for aspirational filter)
  const filteredStateDist = useMemo(() =>
    aspirationalOnly ? districtData.filter(d => d.is_aspirational) : districtData,
    [districtData, aspirationalOnly]
  );
  const filteredDistLookup = useMemo(() => buildDistrictLookup(filteredStateDist), [filteredStateDist]);
  const blockLookup    = useMemo(() => buildBlockLookup(blockData),    [blockData]);
  const blockDataMap   = useMemo(() => buildBlockDataMap(blockData),   [blockData]);

  // All-India district view lookups (state-aware to avoid duplicate shapeName collisions)
  const filteredDistrictData = useMemo(() =>
    aspirationalOnly ? allDistrictData.filter(d => d.is_aspirational) : allDistrictData,
    [allDistrictData, aspirationalOnly]
  );
  const allDistLookup   = useMemo(() => buildAllDistrictLookup(filteredDistrictData), [filteredDistrictData]);
  const allDistDataMap  = useMemo(() => buildAllDistrictDataMap(filteredDistrictData), [filteredDistrictData]);

  // Reverse map: shapeID → stateKey (lowercase) — for disambiguating same-name districts across states
  const shapeIdToState = useMemo(() => {
    const m = {};
    Object.entries(stateDistrictsMap).forEach(([stateKey, stateData]) => {
      (stateData.shapeIDs || []).forEach(id => { m[id] = stateKey; });
    });
    return m;
  }, []);

  const stateColor    = useMemo(() => makeScale(stateLookup, colorMode),    [stateLookup, colorMode]);
  const districtColor = useMemo(() => makeScale(filteredDistLookup, colorMode), [filteredDistLookup, colorMode]);
  const blockColor    = useMemo(() => makeScale(blockLookup, colorMode),    [blockLookup, colorMode]);
  const allDistColor  = useMemo(() => makeScale(allDistLookup, colorMode),  [allDistLookup, colorMode]);

  // ShapeID sets for filtering districts to the selected state only — memoized
  const allowedDistrictIds = useMemo(
    () => selectedState ? new Set(stateDistrictsMap[normalizeGeoName(selectedState)]?.shapeIDs || []) : null,
    [selectedState]
  );

  // ShapeID set for filtering blocks to the selected district only
  const allowedBlockIds = useMemo(() => {
    if (!selectedDistShapeID) return null;
    const ids = districtBlocksMap[selectedDistShapeID];
    return ids ? new Set(ids) : null;
  }, [selectedDistShapeID]);

  // ── event handlers ─────────────────────────────────────────────────────────
  const handleStateClick = (geo) => {
    const norm = normalizeGeoName(geo.properties.shapeName);
    const match = stateData.find(d => normalizeGeoName(d.state) === norm);
    if (match && onStateSelect) onStateSelect(match.state);
  };

  const handleDistrictClick = (geo) => {
    const topoName = geo.properties.shapeName;
    const normTopo = normalizeGeoName(topoName);
    // Reverse lookup: which DB district maps to this shape?
    let dbDistrict = topoName;
    for (const [db, topo] of Object.entries(DISTRICT_DB_TO_TOPO)) {
      if (topo && normalizeGeoName(topo) === normTopo) {
        dbDistrict = db.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
        break;
      }
    }
    // Use pre-computed centroid (from the shape name in the GeoJSON)
    const centroid = districtCentroids[topoName.toLowerCase().trim()]
      || districtCentroids[normTopo]
      || getDistrictCentroid(dbDistrict);
    if (centroid) setDistrictCenter(centroid);
    if (onDistrictSelect) onDistrictSelect(dbDistrict, geo.properties.shapeID);
  };

  const fmtVal = (v) => fmtCr(v);

  // ── Throttled tooltip: max 1 React state update per 40ms regardless of mouse speed ──
  const showTooltip = useCallback((name, val, fullData = null) => {
    if (tooltipThrottleRef.current) return;  // drop rapid calls
    setTooltip({ name, val: val !== undefined && val > 0 ? fmtVal(val) : "No data" });
    setTooltipData(fullData);
    tooltipThrottleRef.current = setTimeout(() => {
      tooltipThrottleRef.current = null;
    }, 40);
  }, []);

  const hideTooltip = useCallback(() => {
    if (tooltipThrottleRef.current) clearTimeout(tooltipThrottleRef.current);
    tooltipThrottleRef.current = null;
    setTooltip(null);
    setTooltipData(null);
  }, []);

  // ── legend ────────────────────────────────────────────────────────────────
  const activeLookup = level === "block" ? blockLookup
    : level === "district" ? districtLookup
    : level === "all-districts" ? allDistLookup
    : stateLookup;
  const maxVal = Math.max(...Object.values(activeLookup).filter(v => v > 0), 1);
  const hasEstimates = level === "state" && stateData.some(d => d.is_estimate);

  return (
    <div className="relative select-none">
      {/* Breadcrumb + controls */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-nowrap">
        <nav aria-label="Breadcrumb" className="flex items-center flex-nowrap gap-y-1 min-w-0" data-testid="map-breadcrumb">
          {level !== "state" && (
            <>
              <button onClick={() => { onStateSelect(null); }}
                data-testid="map-back-btn"
                title="Back to All India"
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-[#B71C1C] hover:bg-[#B71C1C] hover:text-white transition-colors text-[11px] font-semibold whitespace-nowrap">
                <ChevronLeft size={13} />
                All India
              </button>
              <ChevronRight size={12} className="mx-1.5 text-slate-300 flex-shrink-0" />
            </>
          )}
          {level !== "state" && (
            <button
              onClick={() => { if (level === "block") onDistrictSelect(null); else onStateSelect(selectedState); }}
              title={level === "block" ? `Back to ${selectedState}` : selectedState}
              className={`px-2 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap transition-colors
                ${level === "block"
                  ? "bg-red-50 text-[#B71C1C] hover:bg-[#B71C1C] hover:text-white"
                  : "bg-slate-100 text-slate-800"}`}>
              {selectedState}
            </button>
          )}
          {level === "block" && (
            <>
              <ChevronRight size={12} className="mx-1.5 text-slate-300 flex-shrink-0" />
              <span className="px-2 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap bg-slate-100 text-slate-800">
                {selectedDistrict}
                <span className="ml-1 text-slate-500 font-normal">· Blocks</span>
              </span>
            </>
          )}
          {level === "district" && (
            <span className="ml-2 text-[10px] text-slate-500 italic whitespace-nowrap">
              click a district to drill down
            </span>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* State / District toggle — only visible at top level (no drill-down active) */}
          {!selectedState && onMapViewChange && (
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="map-view-toggle">
              <button onClick={() => onMapViewChange("states")} data-testid="map-view-states"
                className={`text-[10px] px-3 py-1 rounded-md font-medium transition-all ${mapViewMode === "states" ? "bg-white text-[#B71C1C] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                States
              </button>
              <button onClick={() => onMapViewChange("districts")} data-testid="map-view-districts"
                className={`text-[10px] px-3 py-1 rounded-md font-medium transition-all ${mapViewMode === "districts" ? "bg-white text-[#B71C1C] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                Districts
              </button>
            </div>
          )}
          {/* Aspirational districts only checkbox — visible in any district-level view */}
          {(level === "all-districts" || level === "district") && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="aspirational-only-filter">
              <input type="checkbox" checked={aspirationalOnly}
                onChange={e => setAspirationalOnly(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-[#B71C1C] focus:ring-[#B71C1C] cursor-pointer accent-[#B71C1C]" />
              <span className="text-[10px] font-medium text-slate-600 whitespace-nowrap">Aspirational districts only</span>
            </label>
          )}
          {/* Proportional estimate note in header row — shown once, not in every hover */}
          {hasEstimates && level === "state" && (
            <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 leading-tight whitespace-nowrap">
            * Sanctioned &amp; disbursement for multi-state grants are proportional estimates
            </span>
          )}
          {/* Zoom controls — inline in the same header row */}
          {showZoomControls && (
            <div className="flex items-center gap-1 ml-1" data-testid="map-zoom-controls">
              <button onClick={() => setUserScale(s => Math.min(s * 1.6, 16))} data-testid="map-zoom-in"
                className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" title="Zoom in">
                <ZoomIn size={12} />
              </button>
              <button onClick={() => setUserScale(s => Math.max(s / 1.6, 0.25))} data-testid="map-zoom-out"
                className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" title="Zoom out">
                <ZoomOut size={12} />
              </button>
              <button onClick={() => setUserScale(1)} data-testid="map-zoom-reset"
                className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" title="Reset zoom">
                <RotateCcw size={12} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Map canvas */}
      <div className="relative rounded-xl overflow-hidden border border-slate-200" style={{ height: 480 }}>
        {/* Loading overlay — shows during level transitions AND while API is fetching data */}
        {(mapLoading || dataLoading) && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.92)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="map-loading-overlay">
            <div style={{ textAlign: 'center' }}>
              <Loader2 size={36} className="text-[#B71C1C] animate-spin" style={{ margin: '0 auto 8px' }} />
              <span className="text-sm font-medium text-slate-600">
                {dataLoading ? (level === "block" ? "Fetching block data…" : "Fetching district data…") : loadingLabel}
              </span>
            </div>
          </div>
        )}

        <ComposableMap
          projection={projFn}
          width={820} height={480}
          style={{ width: "100%", height: "100%", background: "#F8FAFC" }}
        >

            {/* ── LEVEL 0: State choropleth ── */}
            <Geographies geography={STATES_URL}>
              {({ geographies }) => {
                return geographies.map(geo => {
                const norm = normalizeGeoName(geo.properties.shapeName);
                const val  = stateLookup[norm];
                const fullData = stateDataMap[norm]; // Get full state data for rich tooltip
                const isSelectedState = (level === "district" || level === "block") &&
                  normalizeGeoName(selectedState || "") === norm;

                if (level === "state") {
                  // Normal state choropleth
                  return (
                    <Geography key={geo.rsmKey} geography={geo}
                      onClick={() => handleStateClick(geo)}
                      onMouseEnter={() => showTooltip(geo.properties.shapeName, val, fullData)}
                      onMouseLeave={() => hideTooltip()}
                      style={{
                        default:  {
                          fill: stateColor(val || 0),
                          stroke: "#fff",
                          strokeWidth: 0.8,
                          strokeDasharray: "6,3",
                          outline: "none",
                        },
                        hover: {
                          fill: stateColor(val || 0),
                          stroke: "#334155",
                          strokeWidth: 2,
                          strokeDasharray: "none",
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed:  { outline: "none" },
                      }}
                    />
                  );
                }

                if (level === "all-districts") {
                  // All-district view: districts are colored separately, state outlines rendered after
                  return null;
                }

                // District / block level: show only selected state outline
                if (!isSelectedState) return null;
                return (
                  <Geography key={geo.rsmKey} geography={geo}
                    style={{
                      default: {
                        fill:        "none",
                        stroke:      "#94A3B8",
                        strokeWidth: level === "block" ? 0.8 : 1.5,
                        outline: "none",
                      },
                      hover:   { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              });
              }}
            </Geographies>

            {/* ── ALL-DISTRICTS VIEW: All India district choropleth ── */}
            {level === "all-districts" && (
              <Geographies geography={DISTRICTS_URL}>
                {({ geographies }) => {
                  return geographies.map(geo => {
                    const norm = normalizeGeoName(geo.properties.shapeName);
                    const stateKey = shapeIdToState[geo.properties.shapeID] || "";
                    const lookupKey = `${stateKey}|${norm}`;
                    const val      = allDistLookup[lookupKey];
                    const fullData = allDistDataMap[lookupKey];
                    return (
                      <Geography key={geo.rsmKey} geography={geo}
                        onClick={() => {
                          // Find the state for this district and drill into it
                          if (fullData && fullData.state && onStateSelect) {
                            onStateSelect(fullData.state);
                          }
                        }}
                        onMouseEnter={() => {
                          const label = fullData
                            ? `${geo.properties.shapeName} (${fullData.state})`
                            : geo.properties.shapeName;
                          showTooltip(label, val, fullData);
                        }}
                        onMouseLeave={() => hideTooltip()}
                        style={{
                          default: {
                            fill: allDistColor(val || 0),
                            stroke: "#fff",
                            strokeWidth: 0.3,
                            outline: "none",
                          },
                          hover: {
                            fill: allDistColor(val || 0),
                            stroke: "#334155",
                            strokeWidth: 1.5,
                            outline: "none",
                            cursor: "pointer",
                          },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  });
                }}
              </Geographies>
            )}

            {/* State border overlay for all-districts view (rendered after districts so borders are visible) */}
            {level === "all-districts" && (
              <Geographies geography={STATES_URL}>
                {({ geographies }) =>
                  geographies.map(geo => (
                    <Geography key={`outline-${geo.rsmKey}`} geography={geo}
                      style={{
                        default: { fill: "none", stroke: "#64748B", strokeWidth: 1, outline: "none", pointerEvents: "none" },
                        hover:   { outline: "none" },
                        pressed: { outline: "none" },
                      }}
                    />
                  ))
                }
              </Geographies>
            )}

            {/* ── LEVEL 1: District choropleth (state selected) ── */}
            {(level === "district" || level === "block") && allowedDistrictIds && (
              <Geographies geography={DISTRICTS_URL}>
                {({ geographies }) => {
                  const filtered = geographies.filter(geo => allowedDistrictIds.has(geo.properties.shapeID));
                  return filtered.map(geo => {
                    const norm = normalizeGeoName(geo.properties.shapeName);
                    const val  = filteredDistLookup[norm];
                    const isSelectedDistrict = level === "block" &&
                      normalizeGeoName(selectedDistrict || "") === norm;

                    if (level === "block") {
                      // Only render the selected district outline, hide all others
                      if (!isSelectedDistrict) return null;
                      return (
                        <Geography key={geo.rsmKey} geography={geo}
                          style={{
                            default: {
                              fill:        "none",
                              stroke:      "#94A3B8",
                              strokeWidth: 1.2,
                              strokeDasharray: "4,2",
                              outline: "none",
                            },
                            hover:   { outline: "none" },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    }

                    return (
                      <Geography key={geo.rsmKey} geography={geo}
                        onClick={() => handleDistrictClick(geo)}
                        onMouseEnter={() => showTooltip(geo.properties.shapeName, val, districtDataMap[normalizeGeoName(geo.properties.shapeName)])}
                        onMouseLeave={() => hideTooltip()}
                        style={{
                          default: {
                            fill:        districtColor(val || 0),
                            stroke:      "#fff",
                            strokeWidth: 0.8,
                            strokeDasharray: "6,3",
                            outline:     "none",
                          },
                          hover: {
                            fill: districtColor(val || 0),
                            stroke: "#334155",
                            strokeWidth: 2,
                            strokeDasharray: "none",
                            outline: "none",
                            cursor: "pointer",
                          },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                    });
                  }}
              </Geographies>
            )}

            {/* ── LEVEL 2: Block choropleth (district selected) ── */}
            {level === "block" && blocksUrl && (
              <Geographies geography={blocksUrl}>
                {({ geographies }) => {
                  const filtered = allowedBlockIds
                    ? geographies.filter(geo => allowedBlockIds.has(geo.properties.shapeID))
                    : geographies;
                  return filtered.map(geo => {
                    const norm = normalizeGeoName(geo.properties.shapeName);
                    const val  = blockLookup[norm];
                    const fillColor = blockColor(val || 0);
                    return (
                      <Geography key={geo.rsmKey} geography={geo}
                        onMouseEnter={() => showTooltip(geo.properties.shapeName, val, blockDataMap[normalizeGeoName(geo.properties.shapeName)])}
                        onMouseLeave={() => hideTooltip()}
                        style={{
                          default: {
                            fill:        fillColor,
                            stroke:      "#fff",
                            strokeWidth: 0.6,
                            strokeDasharray: "4,2",
                            outline:     "none",
                          },
                          hover: {
                            fill: fillColor,
                            stroke: "#334155",
                            strokeWidth: 1.5,
                            strokeDasharray: "none",
                            outline: "none",
                            cursor: "pointer",
                          },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  });
                }}
              </Geographies>
            )}

        </ComposableMap>

        {/* Fixed info box (bottom-right, outside map area) - Rich Tooltip */}
        <div className="absolute bottom-2 right-2 bg-white/98 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 z-20 pointer-events-none transition-all duration-150"
          style={{ width: 240 }}
          data-testid="map-info-box">
          
          {/* Header */}
          <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50/80 rounded-t-lg">
            <div className="text-[9px] text-slate-600 uppercase tracking-wider font-medium">
              {level === "block" ? "Block" : (level === "district" || level === "all-districts") ? "District" : "State"}
            </div>
            {tooltip ? (
              <div className="text-sm font-bold text-slate-800 leading-tight truncate">{tooltip.name}</div>
            ) : (
              <div className="text-xs text-slate-600 italic">Hover over a region</div>
            )}
          </div>
          
          {/* Rich data — unified for state / district / block */}
          {tooltip && tooltipData ? (
            <div className="px-3 py-2 space-y-1.5">
              {/* Aspirational badge for district / block / all-districts */}
              {(level === "district" || level === "block" || level === "all-districts") && tooltipData.is_aspirational && (
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-medium">
                  ★ Aspirational {level === "block" ? "Block" : "District"}
                </div>
              )}

              {/* Sanctioned & Disbursement */}
              <div className={`grid ${showDisbursed ? "grid-cols-2" : "grid-cols-1"} gap-2 text-xs`}>
                <div>
                  <div className="text-slate-600 text-[9px] uppercase">Sanctioned</div>
                  <div className="font-semibold text-slate-800">{fmtCr(tooltipData.allocation_cr)}</div>
                  {level === "state" && tooltipData.pan_india_share_count > 0 && tooltipData.pan_india_share_cr > 0 && (
                    <div
                      className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[9px] font-medium"
                      data-testid="pan-india-share-badge"
                      title={`Includes ₹${tooltipData.pan_india_share_cr.toFixed(2)} Cr apportioned from ${tooltipData.pan_india_share_count} Pan-India grant${tooltipData.pan_india_share_count > 1 ? "s" : ""}, distributed equally across all 36 States & UTs.`}
                    >
                      <span className="text-[9px] leading-none">ⓘ</span>
                      <span className="leading-none">
                        ₹{tooltipData.pan_india_share_cr.toFixed(2)} Cr from {tooltipData.pan_india_share_count} Pan-India grant{tooltipData.pan_india_share_count > 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
                {showDisbursed && (
                  <div data-testid="map-tooltip-disbursed">
                    <div className="text-slate-600 text-[9px] uppercase">Disbursed</div>
                    <div className="font-semibold text-slate-800">
                      {fmtCr(tooltipData.disbursement_cr != null ? tooltipData.disbursement_cr : 0)}
                    </div>
                  </div>
                )}
              </div>

              {/* Grants (Grantees field removed per stakeholder direction Sep 10, 2026 — value was blank/misleading) */}
              <div className="text-xs border-t border-slate-100 pt-1.5">
                <div className="text-slate-600 text-[9px] uppercase">Grants</div>
                <div className="font-semibold text-slate-800">{fmtNum(tooltipData.grants)}</div>
              </div>

              {/* Districts & Blocks covered (state level only) */}
              {level === "state" && (
                <div className="text-xs border-t border-slate-100 pt-1.5 space-y-0.5">
                  <div className="text-slate-600">
                    <span className="font-medium">{tooltipData.districts_covered}</span> districts
                    {tooltipData.aspirational_covered > 0 && (
                      <span className="text-amber-600 ml-1">({tooltipData.aspirational_covered} aspirational)</span>
                    )}
                  </div>
                  {tooltipData.blocks_covered > 0 && (
                    <div className="text-slate-500">
                      <span className="font-medium">{tooltipData.blocks_covered}</span> blocks
                    </div>
                  )}
                </div>
              )}

              {/* Top portfolios (themes) */}
              {tooltipData.top_themes && tooltipData.top_themes.length > 0 && (
                <div className="border-t border-slate-100 pt-1.5">
                  <div className="text-[9px] text-slate-600 uppercase mb-1">Portfolios</div>
                  <div className="flex flex-wrap gap-0.5">
                    {tooltipData.top_themes.map((t, i) => (
                      <span key={i}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium text-white"
                        style={{ backgroundColor: getThemeDotColor(t.name) }}>
                        {t.name}
                        <span className="opacity-80 ml-0.5">{t.grants}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : tooltip ? (
            // Simple tooltip for districts/blocks
            <div className="px-3 py-1.5">
              <div className="text-xs text-slate-500">{tooltip.val}</div>
            </div>
          ) : null}
        </div>
      </div>

      {/* No block-level data notice */}
      {level === "block" && !dataLoading && !mapLoading && blockData.length === 0 && (
        <div className="mt-3 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm" data-testid="no-block-data-notice">
          <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <span className="font-medium text-amber-800">No block-level grant data for {selectedDistrict}</span>
            <p className="text-amber-700 text-xs mt-0.5">
              The grant geography for this district only specifies the district name, not individual blocks.
              Block-level coloring is not possible without more granular data in the source records.
            </p>
          </div>
        </div>
      )}

      {/* Colour-mode toggle + Legend */}
      <div className="mt-3" data-testid="map-legend">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">
            {colorMode === "buckets" ? "Sanctioned (₹ Cr buckets)" : "Sanctioned (continuous)"}
          </span>
          {/* Toggle */}
          <div
            className="inline-flex bg-slate-100 rounded-full p-0.5 border border-slate-200"
            data-testid="map-color-mode-toggle"
            role="tablist"
          >
            <button
              type="button"
              onClick={() => setColorMode("buckets")}
              data-testid="map-color-mode-buckets"
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                colorMode === "buckets"
                  ? "bg-white text-[#B71C1C] shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              Buckets
            </button>
            <button
              type="button"
              onClick={() => setColorMode("continuous")}
              data-testid="map-color-mode-continuous"
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                colorMode === "continuous"
                  ? "bg-white text-[#B71C1C] shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              Continuous
            </button>
          </div>
        </div>

        {colorMode === "buckets" ? (
          // ── 7-bucket discrete legend ──────────────────────────────────────
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-700">
            {BUCKET_THRESHOLDS.map((b) => (
              <div key={b.label} className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm border border-slate-200" style={{ backgroundColor: b.color }} />
                <span className="font-medium tabular-nums">{b.label}</span>
              </div>
            ))}
            <span className="text-slate-500 ml-auto">
              {level === "all-districts" ? "district" : level} level · max {fmtCr(maxVal)}
            </span>
          </div>
        ) : (
          // ── Continuous gradient legend ───────────────────────────────────
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Low</span>
            <div className="flex flex-1 h-3 rounded overflow-hidden">
              {HEAT_PALETTE.map((c, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: c }} />
              ))}
            </div>
            <span>High</span>
            <span className="font-medium text-slate-700 ml-1">{fmtCr(maxVal)}</span>
            <span className="text-slate-600">sanctioned ({level === "all-districts" ? "district" : level} level)</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default IndiaMap;
